import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {ShoppingCart, Check, Package, Shield, Thermometer, FileText, Star, ArrowRight} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const TravelEssentials = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const products = [
    {
      id: 1,
      name: "Haven Custom Travel Pill Case",
      category: "Medication Management",
      price: "$24.99",
      featured: true,
      image: "https://static.lumi.new/ca/ca63aab1d9bb27f41cc2e4a05c8bc48c.webp",
      description: "Compact 7-day organizer with AM/PM compartments. TSA-approved, waterproof design.",
      features: ["14 secure compartments", "Compact & lightweight", "Medical-grade plastic", "Travel-friendly"],
      rating: 4.8
    },
    {
      id: 2,
      name: "Compact First Aid Kit",
      category: "Safety",
      price: "$34.99",
      image: "https://static.lumi.new/e4/e43775dc1d1313d7b4236c1cb61a4a19.png",
      description: "Essential first aid supplies for international travel. Includes bandages, antiseptics, and pain relief.",
      features: ["90+ pieces", "Organized compartments", "Portable design", "Travel manual included"],
      rating: 4.7
    },
    {
      id: 3,
      name: "Vaccine Travel Cooler",
      category: "Temperature Control",
      price: "$49.99",
      image: "https://static.lumi.new/9e/9eef1634e737c3a933c78dc6288dcd06.png",
      description: "Insulated cooler bag maintains 2-8°C for temperature-sensitive medications and vaccines.",
      features: ["12-hour cooling", "Medical-grade insulation", "Compact size", "TSA compliant"],
      rating: 4.9
    },
    {
      id: 4,
      name: "Health Document Organizer",
      category: "Documentation",
      price: "$19.99",
      image: "https://static.lumi.new/b6/b666b4e2bc980dd2432e6a3f19c8fe33.png",
      description: "Premium organizer for vaccination cards, prescriptions, and travel health documents.",
      features: ["RFID protection", "Multiple card slots", "Waterproof material", "Passport compatible"],
      rating: 4.6
    },
    {
      id: 5,
      name: "Travel Insect Repellent",
      category: "Protection",
      price: "$16.99",
      image: "https://static.lumi.new/3e/3e57620687f486dd41651e90bbdd75f0.png",
      description: "DEET-based mosquito protection spray. TSA-compliant travel size for tropical destinations.",
      features: ["30% DEET formula", "8-hour protection", "Travel size (100ml)", "Tropical tested"],
      rating: 4.7
    },
    {
      id: 6,
      name: "Premium Hand Sanitizer",
      category: "Hygiene",
      price: "$12.99",
      image: "https://static.lumi.new/98/9887f583a38a1ef124fdfe1023ea7eae.png",
      description: "70% alcohol hand sanitizer with aloe vera. Perfect for travel when soap isn't available.",
      features: ["70% alcohol", "Moisturizing formula", "Leak-proof bottle", "TSA compliant"],
      rating: 4.5
    },
    {
      id: 7,
      name: "Motion Sickness Relief",
      category: "Comfort",
      price: "$22.99",
      image: "https://static.lumi.new/0c/0cc895b18f6ef2a625e415a5b62e283f.png",
      description: "Acupressure wristbands for natural motion sickness relief during flights and sea travel.",
      features: ["Drug-free relief", "Reusable design", "Adjustable fit", "Clinically tested"],
      rating: 4.4
    },
    {
      id: 8,
      name: "Water Purification Tablets",
      category: "Safety",
      price: "$18.99",
      image: "https://static.lumi.new/b9/b967c72e95a027c528adc5aa76db05de.png",
      description: "Portable water treatment tablets. Essential for areas with questionable water quality.",
      features: ["50 tablets included", "Kills 99.9% bacteria", "30-minute treatment", "Compact packaging"],
      rating: 4.8
    },
    {
      id: 9,
      name: "Travel Sunscreen SPF 50+",
      category: "Protection",
      price: "$21.99",
      image: "https://static.lumi.new/1f/1ff0f0a8afe07cbeec2f3a06c4a67eb1.png",
      description: "Broad spectrum, reef-safe sunscreen. Water-resistant for beach and outdoor activities.",
      features: ["SPF 50+ UVA/UVB", "Reef-safe formula", "Water resistant 80min", "Non-greasy"],
      rating: 4.6
    },
    {
      id: 10,
      name: "Oral Rehydration Salts",
      category: "Health",
      price: "$14.99",
      image: "https://static.lumi.new/c8/c8cfb8c46412db0f1745589963b4bd87.png",
      description: "WHO-formula electrolyte packets for traveler's diarrhea and dehydration prevention.",
      features: ["10 sachets", "WHO formula", "Lemon flavor", "Fast absorption"],
      rating: 4.7
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Quality Assured",
      desc: "Medical-grade materials and professional healthcare standards"
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Fast Shipping",
      desc: "Ships within 24-48 hours across Canada"
    },
    {
      icon: <Check className="w-6 h-6" />,
      title: "Satisfaction Guarantee",
      desc: "30-day money-back guarantee on all products"
    }
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center">
            <span className="inline-block px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-semibold mb-6">
              Travel Smart
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Travel Essentials for<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400">
                Safe Journeys
              </span>
            </h1>
            <p className="max-w-2xl mx-auto text-lg text-gray-300 mb-8">
              Curated healthcare products designed specifically for international travelers. Professional-grade quality meets practical design.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-12 bg-white dark:bg-navy-800 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 justify-center md:justify-start">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-bold text-navy-900 dark:text-white mb-1">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-gray-50 dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white dark:bg-navy-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 hover:border-cyan-500/50 transition-all hover:shadow-xl group ${
                  product.featured ? 'lg:col-span-2' : ''
                }`}>
                
                {product.featured && (
                  <div className="absolute top-4 left-4 z-20 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-bold rounded-full shadow-lg">
                    Featured Product
                  </div>
                )}

                <div className={`grid ${product.featured ? 'lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
                  {/* Image/Video */}
                  <div className="relative bg-gray-100 dark:bg-navy-900 aspect-square overflow-hidden">
                    {product.video ? (
                      <video
                        src={product.video}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6 lg:p-8 flex flex-col justify-between">
                    <div>
                      <span className="inline-block px-3 py-1 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 rounded-full text-xs font-semibold mb-4">
                        {product.category}
                      </span>
                      
                      <h3 className="text-2xl font-bold text-navy-900 dark:text-white mb-3">
                        {product.name}
                      </h3>

                      {/* Rating */}
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className={i < Math.floor(product.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {product.rating} / 5.0
                        </span>
                      </div>

                      <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                        {product.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-2 mb-6">
                        {product.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                            <Check size={16} className="text-cyan-500 shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Price & CTA */}
                    <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-white/10">
                      <div>
                        <div className="text-3xl font-bold text-navy-900 dark:text-white">
                          {product.price}
                        </div>
                        <div className="text-sm text-gray-500">Free shipping over $50</div>
                      </div>
                      <button className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-xl shadow-lg shadow-cyan-500/25 transition-all hover:scale-105 flex items-center gap-2">
                        <ShoppingCart size={18} />
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="py-20 bg-white dark:bg-navy-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                Not Sure What You Need?
              </h2>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                Book a virtual consultation with our travel health specialists to get personalized recommendations for your trip.
              </p>
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-navy-900 font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                Book Virtual Consult
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default TravelEssentials;